import { useMemo } from "react";

import type { ITransformedDomain } from "../utils/transformData";

const COMPACT_ROW_COUNT = 5;

interface ISourceData {
    domain: ITransformedDomain;
    share?: number | null;
}

interface IUseCompactDataParams<D> {
    data?: D[];
    isOthersShareCalculated?: boolean;
}

interface IOthersData {
    domain: ITransformedDomain[];
    share?: number;
}

const addOthersRow = <D extends ISourceData>({
    visibleRows,
    aggregatedOthers,
    isPrimaryDomainInTop,
}: {
    visibleRows: Array<D | IOthersData>;
    aggregatedOthers: { domains: ITransformedDomain[]; shares: number };
    isPrimaryDomainInTop: boolean;
}) => {
    const lastItem = visibleRows[visibleRows.length - 1];
    const lastDomain = lastItem?.domain;
    const isLastItemPrimaryDomain = !Array.isArray(lastDomain) && !!lastDomain?.isPrimaryDomain;
    const othersRow = {
        domain: aggregatedOthers.domains,
        share: aggregatedOthers.shares,
    };

    if (isLastItemPrimaryDomain && othersRow.share > (lastItem.share ?? 0) && !isPrimaryDomainInTop) {
        return visibleRows.toSpliced(visibleRows.length - 1, 0, othersRow);
    }

    visibleRows.push(othersRow);

    return visibleRows;
};

export const useCompactData = <D extends ISourceData>({ data, isOthersShareCalculated }: IUseCompactDataParams<D>) =>
    useMemo(() => {
        if (!data) {
            return [];
        }

        if (data.length <= COMPACT_ROW_COUNT) {
            return data;
        }

        const visibleRows: Array<D | IOthersData> = [];
        const aggregatedOthers: {
            domains: ITransformedDomain[];
            shares: number;
        } = {
            domains: [],
            shares: 0,
        };
        const isPrimaryDomainInTop = data.slice(0, COMPACT_ROW_COUNT - 1).some((item) => item.domain.isPrimaryDomain);
        const maxVisibleRows = isPrimaryDomainInTop ? COMPACT_ROW_COUNT - 1 : COMPACT_ROW_COUNT - 2;

        for (const item of data) {
            if (item.domain.isPrimaryDomain) {
                visibleRows.push(item);
                continue;
            }

            if (visibleRows.length < maxVisibleRows) {
                visibleRows.push(item);
                continue;
            }

            aggregatedOthers.domains.push(item.domain);
            aggregatedOthers.shares += item.share != null ? item.share : 0;
        }

        if (isOthersShareCalculated) {
            return addOthersRow({
                visibleRows,
                aggregatedOthers,
                isPrimaryDomainInTop,
            });
        }

        visibleRows.push({
            domain: aggregatedOthers.domains,
        });

        return visibleRows;
    }, [data, isOthersShareCalculated]);
